/**
 * Profile Service - Servizi per gestione profilo utente
 */

import { protectedApi, publicApi } from './api';
import { API_ENDPOINTS } from '../config/apiConfig';
import { STORAGE_KEYS } from '../utils/constants';

// Decodifica ricorsivamente tutte le stringhe in un oggetto/array.
// Usa un elemento textarea per sfruttare il parser HTML nativo del browser
// (gestisce &agrave; &egrave; &#39; ecc. senza librerie esterne).
const decodeEntities = (() => {
  const el = document.createElement('textarea');
  const decode = (str) => { el.innerHTML = str; return el.value; };
  const walk = (val) => {
    if (typeof val === 'string') return decode(val);
    if (Array.isArray(val))     return val.map(walk);
    if (val && typeof val === 'object') {
      const out = {};
      for (const k of Object.keys(val)) out[k] = walk(val[k]);
      return out;
    }
    return val;
  };
  return walk;
})();

/**
 * Recupera i dati anagrafici dell'utente dal CRM
 * @param {number} idAnagrafico - ID anagrafico dell'utente
 * @returns {Promise<Object>} Dati anagrafica
 */
export const getAnagraficaProfile = async (idAnagrafico) => {
  try {
    if (!idAnagrafico) {
      throw new Error('idAnagrafico è obbligatorio per recuperare i dati anagrafica');
    }
    console.log(`[profileService] Chiamata GET /api/Profile/anagrafica/${idAnagrafico}`);
    const response = await protectedApi.get(`${API_ENDPOINTS.PROFILE.ANAGRAFICA}/${idAnagrafico}`);

    console.log('[profileService] Risposta anagrafica:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Recupero anagrafica fallito');
    }

    // ✅ REFACTORED: Ritorna solo i dati, senza side-effects su localStorage
    // AuthContext gestisce automaticamente la sincronizzazione con localStorage
    console.log('[profileService] Anagrafica recuperata con successo');
    return { success: true, data: decodeEntities(data), message, code };
  } catch (error) {
    console.error('[profileService] Errore recupero anagrafica:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante il recupero dei dati anagrafica';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

// ✅ DEPRECATED: getAnagraficaFromStorage e clearAnagraficaFromStorage rimossi
// Usare AuthContext.profile invece di accedere direttamente a localStorage

/**
 * Aggiorna l'elenco delle occupazioni (impieghi) dell'utente
 * ✅ REFACTORED: Ora richiede user e profile come parametri invece di leggerli da localStorage
 * @param {Object} payload - { impieghi: [...] }
 * @param {Object} user - Oggetto user da AuthContext
 * @param {Object} profile - Oggetto profile da AuthContext
 * @returns {Promise<Object>} Risposta aggiornamento
 */
export const updateImpieghi = async (payload, user, profile) => {
  try {
    console.log('[profileService] ===== INIZIO updateImpieghi =====');
    console.log('[profileService] Aggiornamento impieghi - chiamata POST /api/Profile/anagrafica/save');
    console.log('[profileService] Payload impieghi ricevuto:', JSON.stringify(payload, null, 2));

    // ✅ REFACTORED: Usa profile da context invece di localStorage
    if (!profile) {
      throw new Error('Dati anagrafica non trovati. Impossibile aggiornare gli impieghi.');
    }

    // ✅ REFACTORED: Usa user da context invece di localStorage
    const idUtenteSito = user?.id || '';

    console.log('[profileService] User data da context:', user);
    console.log('[profileService] ID utente sito (user.id):', idUtenteSito);

    // ✅ REFACTORED: Usa profile da context
    const contatto = profile.contatto || {};
    const recapiti = contatto.recapiti || {};

    console.log('[profileService] Dati anagrafica da context:', profile);
    console.log('[profileService] ID contatto (contatto.id):', contatto.id);

    // Costruisci il payload completo con struttura registrazione
    // IMPORTANTE: Per la modifica, usa gli STESSI nomi di campo del completamento profilo
    // Il backend si aspetta: data_nascita, luogo_nascita, codice_fiscale, indirizzo, comune, CAP, provincia, regione
    const registrazione = {
      id: parseInt(contatto.id), // ID anagrafica per modifica (sempre in contatto.id) - NUMERO
      id_utente_sito: String(idUtenteSito), // ID account utente (user.id) - STRINGA
      nome: contatto.nome || '',
      cognome: contatto.cognome || '',
      data_nascita: contatto.data_nascita || '',
      luogo_nascita: contatto.luogo_nascita || '',
      codice_fiscale: contatto.codice_fiscale || '',
      titolo_studio: contatto.titolo_studio || '',
      indirizzo: contatto.indirizzo || '',
      comune: contatto.comune || '',
      CAP: contatto.CAP || '',
      provincia: contatto.provincia || '',
      regione: contatto.regione || '',
      recapiti: {
        email: recapiti.email || '',
        cellulare: recapiti.cellulare || '',
      },
      impieghi: payload.impieghi.map(impiego => {
        const impiegoData = {
          id_ente: parseInt(impiego.id_ente), // Converti a numero come nel CompletaProfiloForm
          ruolo: impiego.ruolo || '',
          ufficio: impiego.ufficio || '',
          tipo: impiego.tipo_impiego || impiego.tipo || '',  // Può essere tipo_impiego o tipo
          data_inizio: impiego.data_inizio || '',
          data_fine: impiego.data_fine || '',
        };

        // IMPORTANTE: L'impiego.id è l'ID della RELAZIONE impiego-anagrafica nel CRM
        // - Per MODIFICA di impiego esistente: includi id (ID della relazione dal CRM)
        // - Per INSERIMENTO nuovo impiego: ometti completamente il campo id
        // registrazione.id (nella root) = ID dell'anagrafica/contatto
        // impieghi[].id = ID della relazione impiego-anagrafica (es. 787)
        if (impiego.id) {
          impiegoData.id = parseInt(impiego.id); // Usa l'ID della relazione impiego
        }

        return impiegoData;
      }),
    };

    const payloadFinale = { registrazione };
    console.log('[profileService] Payload FINALE per aggiornamento impieghi:', JSON.stringify(payloadFinale, null, 2));

    const response = await protectedApi.post(
      `${API_ENDPOINTS.PROFILE.ANAGRAFICA}/save`,
      payloadFinale
    );

    console.log('[profileService] Risposta aggiornamento impieghi:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Aggiornamento impieghi fallito');
    }

    // ✅ REFACTORED: Ritorna solo i dati, senza side-effects su localStorage
    // Il componente chiamante deve chiamare refreshUser() per aggiornare il context
    console.log('[profileService] Impieghi aggiornati con successo');
    return { success: true, data, message, code };
  } catch (error) {
    console.error('[profileService] Errore aggiornamento impieghi:', error);
    console.error('[profileService] Response data:', error.response?.data);
    console.error('[profileService] Response status:', error.response?.status);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante l\'aggiornamento delle occupazioni';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Salva i dati completi del profilo (anagrafica + contatti + impieghi)
 * Utilizzato per il completamento iniziale del profilo
 * @param {Object} registrazione - Dati completi registrazione
 * @returns {Promise<Object>} Risposta salvataggio
 */
export const saveAnagraficaCompleta = async (registrazione) => {
  try {
    console.log('[profileService] Chiamata POST /api/Profile/anagrafica/save');
    console.log('[profileService] Payload registrazione (prima del wrap):', registrazione);

    const payloadFinale = { registrazione };
    console.log('[profileService] Payload FINALE inviato:', JSON.stringify(payloadFinale, null, 2));

    const response = await protectedApi.post(
      `${API_ENDPOINTS.PROFILE.ANAGRAFICA}/save`,
      payloadFinale
    );

    console.log('[profileService] Risposta salvataggio anagrafica:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Salvataggio anagrafica fallito');
    }

    // ✅ REFACTORED: Ritorna solo i dati, senza side-effects su localStorage
    // Il componente chiamante deve chiamare refreshUser() per aggiornare il context
    console.log('[profileService] Anagrafica completa salvata con successo');
    return { success: true, data, message, code };
  } catch (error) {
    console.error('[profileService] Errore salvataggio anagrafica completa:', error);
    console.error('[profileService] Response data:', error.response?.data);
    console.error('[profileService] Response status:', error.response?.status);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante il salvataggio dei dati profilo';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Azzera l'idAnagrafico dell'utente corrente.
 * Da chiamare quando il record CRM non esiste più (risposta 500 da /api/Profile/anagrafica/{id}).
 * Dopo questa chiamata eseguire refreshUser() per ottenere un nuovo JWT senza idAnagrafico.
 * @returns {Promise<Object>} { success, code, message }
 */
export const resetIdAnagrafico = async () => {
  try {
    console.log('[profileService] Chiamata DELETE /api/Profile/anagrafica/reset');
    const response = await protectedApi.delete(`${API_ENDPOINTS.PROFILE.ANAGRAFICA}/reset`);
    const { success, code, message } = response.data;
    console.log('[profileService] Reset idAnagrafico:', { success, code, message });
    return { success, code, message };
  } catch (error) {
    console.error('[profileService] Errore reset idAnagrafico:', error);
    const code = error.response?.status || 500;
    const message = error.response?.data?.message || 'Errore durante il reset del profilo';
    return { success: false, code, message };
  }
};

/**
 * Verifica se l'utente corrente ha già preso visione dell'informativa privacy
 * @returns {Promise<Object>} { success, data: { hasAccepted, dataPresaVisione }, message, code }
 */
export const getPresaVisioneStatus = async () => {
  try {
    const response = await protectedApi.get(API_ENDPOINTS.PROFILE.PRESA_VISIONE_STATUS);
    const { data, code, success, message } = response.data;
    if (!success || code !== 200) throw new Error(message || 'Verifica presa visione fallita');
    return { success: true, data, message, code };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Errore verifica presa visione';
    const code = error.response?.data?.code || error.response?.status || 500;
    return { success: false, data: null, message: errorMessage, code };
  }
};

/**
 * Segnala un'anomalia per un collaboratore
 * @param {Object} anomaliaData - Dati dell'anomalia da segnalare
 * @param {string} anomaliaData.id_utente_sito - ID utente sito
 * @param {number} anomaliaData.id - ID anagrafica
 * @param {string} anomaliaData.nome - Nome
 * @param {string} anomaliaData.cognome - Cognome
 * @param {string} anomaliaData.codice_fiscale - Codice fiscale
 * @param {string} anomaliaData.titolo_studio - Titolo studio
 * @param {string} anomaliaData.email - Email
 * @param {Array} anomaliaData.impieghi - Array di impieghi ({ id, id_ente, ruolo, ufficio, data_inizio, data_fine, tipo })
 * @returns {Promise<Object>} Response con format {success, code, message, data, timestamp}
 */
export const sendAnomalia = async (anomaliaData) => {
  try {
    console.log('[profileService] Invio anomalia:', anomaliaData);

    const response = await protectedApi.post(API_ENDPOINTS.PROFILE.SEND_ANOMALIA, {
      anomalia: anomaliaData,
    });

    console.log('[profileService] Risposta invio anomalia:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Segnalazione anomalia fallita');
    }

    return { success: true, data, message, code };
  } catch (error) {
    console.error('[profileService] Errore segnalazione anomalia:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante la segnalazione dell\'anomalia';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Verifica se esiste già un profilo CRM con il codice fiscale specificato.
 * Utilizzato durante il completamento profilo per prevenire duplicati.
 * @param {string} codiceFiscale
 * @returns {Promise<{ success: boolean, exists: boolean, message: string }>}
 */
export const verificaCodiceFiscale = async (codiceFiscale) => {
  try {
    const cf = encodeURIComponent(codiceFiscale.trim().toUpperCase());
    const response = await protectedApi.get(`/api/Profile/verifica-cf/${cf}`);
    const { data, message } = response.data;
    return { success: true, exists: data?.exists === true, message };
  } catch (error) {
    console.error('[profileService] Errore verifica CF:', error);
    return { success: false, exists: false, message: error.message };
  }
};

/**
 * Verifica nella cache crm:contatti se esiste già un contatto con lo stesso CF E la stessa email,
 * escludendo il record corrente (identificato da excludeId = idAnagrafico dell'utente).
 * Da chiamare in AnagraficaEditForm prima del salvataggio, solo se CF o email sono stati modificati.
 * @param {string} cf - Codice fiscale
 * @param {string} email - Email
 * @param {number} excludeId - idAnagrafico dell'utente corrente (escluso dalla ricerca)
 * @returns {Promise<{ success: boolean, exists: boolean, message: string }>}
 */
export const verificaDuplicatoCfEmail = async (cf, email, excludeId) => {
  try {
    const params = new URLSearchParams({
      cf: cf.trim().toUpperCase(),
      email: email.trim().toLowerCase(),
      excludeId: String(excludeId),
    });
    const response = await protectedApi.get(
      `${API_ENDPOINTS.PROFILE.VERIFICA_DUPLICATO}?${params.toString()}`
    );
    const { data, message } = response.data;
    return { success: true, exists: data?.exists === true, message };
  } catch (error) {
    console.error('[profileService] Errore verifica duplicato CF+email:', error);
    // In caso di errore di rete/server, non bloccare il salvataggio (fail-open)
    return { success: false, exists: false, message: error.message };
  }
};

/**
 * Recupera i thumbnail avatar per una lista di ID contatto CRM.
 * Endpoint pubblico (AllowAnonymous) — usa publicApi.
 * @param {string[]} ids - Array di ID contatto CRM
 * @returns {Promise<{ success: boolean, data: Object }>} data = { [id]: imagePath }
 */
export const getContattiThumbnails = async (ids = []) => {
  try {
    const params = ids.length > 0 ? { ids: ids.join(',') } : {};
    const response = await publicApi.get(API_ENDPOINTS.PROFILE.CONTATTI_THUMBNAILS, { params });
    const { data, success } = response.data;
    if (!success) return { success: false, data: {} };
    return { success: true, data: data || {} };
  } catch {
    return { success: false, data: {} };
  }
};
